import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { settingsAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/Settings.css';

const Settings = () => {
    const { theme, toggleTheme } = useTheme();
    const [settings, setSettings] = useState({
        theme: 'light',
        notification_sound: 'default',
        reminder_interval: 15,
        always_on_top: true
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const response = await settingsAPI.get();
            setSettings(response.data.settings);
            setLoading(false);
        } catch (error) {
            console.error('Error loading settings:', error);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setSettings({ ...settings, [e.target.name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');

        try {
            await settingsAPI.update(settings);
            setMessage('Settings saved successfully!');
            
            // Update theme if changed
            if (settings.theme !== theme) {
                toggleTheme();
            }
            
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            setMessage('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <>
                <Sidebar />
                <div className="main-content">
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <div className="main-content">
                <div className="settings-container">
                <h1>⚙️ Settings</h1>

                {message && (
                    <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
                        {message}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="settings-form">
                    <div className="settings-section">
                        <h2>🎨 Appearance</h2>
                        
                        <div className="form-group">
                            <label htmlFor="theme">Theme</label>
                            <select 
                                id="theme" 
                                name="theme" 
                                value={settings.theme} 
                                onChange={handleInputChange}
                            >
                                <option value="light">Light</option>
                                <option value="dark">Dark</option>
                            </select>
                            <small>Choose your preferred color scheme</small>
                        </div>
                    </div>

                    <div className="settings-section">
                        <h2>🔔 Notifications</h2>
                        
                        <div className="form-group">
                            <label htmlFor="notification_sound">Notification Sound</label>
                            <select 
                                id="notification_sound" 
                                name="notification_sound" 
                                value={settings.notification_sound} 
                                onChange={handleInputChange}
                            >
                                <option value="default">Default</option>
                                <option value="chime">Chime</option>
                                <option value="bell">Bell</option>
                                <option value="ding">Ding</option>
                                <option value="none">None (Silent)</option>
                            </select>
                            <small>Sound to play when receiving notifications</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="reminder_interval">Reminder Interval (minutes)</label>
                            <input
                                type="number"
                                id="reminder_interval"
                                name="reminder_interval"
                                value={settings.reminder_interval}
                                onChange={handleInputChange}
                                min="1"
                                max="120"
                            />
                            <small>How often to remind you when the app is in the background (1-120 minutes)</small>
                        </div>
                    </div>

                    <div className="settings-section">
                        <h2>🖥️ Window Behavior</h2>
                        
                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="always_on_top"
                                    checked={settings.always_on_top}
                                    onChange={handleInputChange}
                                />
                                <span>Always keep window on top</span>
                            </label>
                            <small>Keep the TaskFocus window visible above other applications</small>
                        </div>
                    </div>

                    <div className="settings-section">
                        <h2>ℹ️ About</h2>
                        <div className="about-info">
                            <p><strong>TaskFocus</strong> - Version 1.0.0</p>
                            <p>A comprehensive task management application to help you stay focused.</p>
                            <p>Open source project - contributions welcome!</p>
                        </div>
                    </div>

                    <div className="settings-actions">
                        <button type="submit" className="btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </>
    );
};

export default Settings;
